import { Injectable } from '@angular/core';
import { BaseService } from './base.service';
import { HttpClient } from '@angular/common/http';
import { ConstantsService } from './constants.service';
import { environment } from '../../environments/environment';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, ReplaySubject, tap } from 'rxjs';
import { FolderModel } from '../models/folder-model';
import { AppSettingService } from './app-setting.service';

@Injectable({
  providedIn: 'root'
})
export class FoldersService extends BaseService {

  private selectedFolderSubject = new BehaviorSubject<string>('');
  public selectedFolder$ = this.selectedFolderSubject.asObservable();

  private selectedLibrarySubject = new BehaviorSubject<string>('');
  public selectedLibrary$ = this.selectedLibrarySubject.asObservable();

  private ehdFoldersSubject = new ReplaySubject<FolderModel[]>(1);
  ehdFolders$ = this.ehdFoldersSubject.asObservable();

  private sdmFoldersSubject = new ReplaySubject<FolderModel[]>(1);
  sdmFolders$ = this.sdmFoldersSubject.asObservable();

  private foldersLoadedSubject = new BehaviorSubject<boolean>(false);
  foldersLoaded$ = this.foldersLoadedSubject.asObservable();

  constructor(public override http: HttpClient, private appSvc: AppSettingService,
    public override constants: ConstantsService) {
    super(http, constants, constants.api_fldr);
    this.setSelectedLibrary(constants.indexEHD);
  }

  // private safeParse = <T>(v: unknown): T => {
  //   typeof v === 'string' ? JSON.parse(v) as T: (v as T);
  // };

  loadInitialFolders(): Observable<boolean> {
    return forkJoin({
      ehd: this.appSvc.getFolders(this.constants.indexEHD),
      sdm: this.appSvc.getFolders(this.constants.indexSDM)
    }).pipe(
      map(({ ehd, sdm }) => {
        const e = JSON.parse(ehd.settingValue);
        const s = JSON.parse(sdm.settingValue);
        // console.log('ehd: ' + JSON.stringify(e));
        // console.log('sdm: ' + JSON.stringify(e));
        return { e, s } as { e: FolderModel[], s: FolderModel[] };
      }),
      tap((fldrs) => {
        this.setLoadedFolders(fldrs.e, fldrs.s);
      }),
      map(() => true),
      catchError((err) => {
        console.error('Error Loading folders: ', err);
        this.setLoadedFolders([], []);
        return of(true);
      })
    );
  }

  private setLoadedFolders(ehd: FolderModel[], sdm: FolderModel[]) {
    // console.log('setting folders.');
    this.ehdFoldersSubject.next(ehd);
    this.sdmFoldersSubject.next(sdm);
    this.foldersLoadedSubject.next(true);
  }

  getSelectedLibrary() {
    return this.selectedLibrarySubject.value;
  }

  setSelectedLibrary(libraryName: string) {
    if (libraryName !== this.selectedLibrarySubject.value) {
      this.selectedLibrarySubject.next(libraryName);
      this.selectedFolderSubject.next('');
    }
  }

  // API Endpoints
  getSubFolders(item: FolderModel): Observable<FolderModel[]> {
    return this.http.post<FolderModel[]>(this.constants.api_fldr_sub + environment.api_code, item)
      .pipe(catchError(this.handleError));
  }

  // Folder Management Methods
  setSelectedFolder(newFolderPath: string) {
    const cleanPath = newFolderPath.startsWith('/') ? newFolderPath.slice(1) : newFolderPath;
    this.selectedFolderSubject.next(cleanPath);
  }

  getSelectedFolder() {
    return this.selectedFolderSubject.value;
  }

  ensureRootFolderSlash(str: string) {
    return str.startsWith('/') ? str : '/' + str;
  }

  getImmediateChildFolders(): FolderModel[] {
    let selectedPath = this.getSelectedFolder();
    selectedPath = this.ensureRootFolderSlash(selectedPath);

    // Assume EHD root
    selectedPath = selectedPath.startsWith(this.constants.base_EHD_folder)
      ? selectedPath.slice(this.constants.base_EHD_folder.length)
      : selectedPath;

    // Check for the SDM root.
    selectedPath = selectedPath.startsWith(this.constants.base_SDM_folder)
      ? selectedPath.slice(this.constants.base_SDM_folder.length)
      : selectedPath;

    const rootTree = this.getSelectedLibrary() === this.constants.indexSDM
      ? this._getSnapshot(this.sdmFoldersSubject)
      : this._getSnapshot(this.ehdFoldersSubject);

    // console.log("rootTree: " + JSON.stringify(rootTree));
    // console.log("selected path: ", selectedPath);

    if (!selectedPath || !rootTree)
      return [];

    //Traverse to the selected node
    const segments = selectedPath.split('/');

    // console.log("segments: ", JSON.stringify(segments));

    let current: FolderModel | undefined = undefined;
    let children = rootTree;

    for (const segment of segments) {
      if (!segment.trim()) continue;

      current = children.find(f => f.name === segment);
      if (!current)
        return [];

      children = current.children || [];
    }

    return children;
  }

  private _getSnapshot(subject: ReplaySubject<FolderModel[]>): FolderModel[] | null {
    let snapshot: FolderModel[] | null = null;

    subject.subscribe(value => snapshot = value).unsubscribe();

    return snapshot;
  }
}
